/**
 * Which detach entries a view-instance tab's context menu offers (§5.8).
 *
 * Split out as a pure function because it encodes a deliberate difference from
 * the activityBar rail: the rail lists a view once and requires an explicit
 * `detachable: true` to offer detaching it, while an instance tab **is** one
 * instance, so popping it out is the tab's own affordance and needs no opt-in.
 * An explicit `detachable: false` still opts out — a module that declared it
 * does not want this view in a window of its own.
 */
export type TabDetachAction = 'detach' | 'focusDetached' | 'attach';

export interface TabDetachMenuInput {
  /** Manifest `detachable`; `undefined` when the view never declared it. */
  readonly detachable?: boolean;
  /** Whether this exact instance is already migrated to its own window. */
  readonly detached: boolean;
  /** Which handlers the host actually wired up. */
  readonly handlers: {
    readonly detach: boolean;
    readonly focusDetached: boolean;
    readonly attach: boolean;
  };
}

export function resolveTabDetachActions({
  detachable,
  detached,
  handlers,
}: TabDetachMenuInput): readonly TabDetachAction[] {
  if (detached) {
    // Never offer to detach twice: the handle is already open, so the only
    // useful moves are reaching that window or bringing it back.
    const actions: TabDetachAction[] = [];
    if (handlers.focusDetached) {
      actions.push('focusDetached');
    }
    if (handlers.attach) {
      actions.push('attach');
    }
    return actions;
  }
  if (detachable === false || !handlers.detach) {
    return [];
  }
  return ['detach'];
}
